/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
  useEffect,
} from 'react';
import type { ReactNode } from 'react';
import type { SavedView, SavedViewModelType } from 'src/api/generated';
import {
  useAllSavedViews,
  useDeleteSavedView,
  useUpdateSavedView,
} from '../hooks/api/saved-views';
import { useHasRoles } from 'src/modules/shared/hooks/auth';
import { useCurrentUser } from 'src/modules/shared/hooks/api/admin/users';
import { useAlert } from 'src/contexts/alert-context';
import Loading from 'src/modules/shared/components/atoms/loading';
import {
  clearSavedViewSelectionPreference,
  persistSavedViewSelectionPreference,
  readSavedViewSelectionPreference,
  SAVED_VIEW_SELECTION_PREFERENCE_CHANGE_EVENT,
  type SavedViewSelectionPreferenceScope,
} from '../utils/saved-view-selection-preference.util';

interface SavedViewContextProps {
  selectedView: SavedView | null;
  setSelectedView: (view: SavedView | null) => void;
  isViewSelected: boolean;
  isCurrentUserViewOwner: boolean;
  hasPermissionToEdit: boolean;
  allViews: SavedView[];
  loading: boolean;
  isAdmin: boolean;
  userViews: SavedView[];
  sharedViews: SavedView[];
  updateView: (view: SavedView) => Promise<SavedView>;
  deleteView: (viewId: number) => Promise<void>;
}

const SavedViewContext = createContext<SavedViewContextProps | undefined>(
  undefined
);

interface SavedViewProviderProps {
  children: ReactNode;
  model: SavedViewModelType;
}

export const SavedViewProvider = ({
  children,
  model,
}: SavedViewProviderProps) => {
  const [selectedView, setSelectedViewState] = useState<SavedView | null>(null);
  const { views, loading: isLoadingViews } = useAllSavedViews(model);
  const { hasRole: isAdmin, loading: isLoadingRoles } = useHasRoles('Admin');
  const { user, loading: isLoadingCurrentUser } = useCurrentUser();
  const { updateView: updateViewMutation } = useUpdateSavedView();
  const { deleteView: deleteViewMutation } = useDeleteSavedView();
  const { showAlert } = useAlert();

  const selectionScope =
    useMemo<SavedViewSelectionPreferenceScope | null>(() => {
      if (!user?.id) {
        return null;
      }

      return {
        userId: user.id,
        model,
      };
    }, [model, user?.id]);

  const syncSelectedViewFromStorage = useCallback(() => {
    if (isLoadingViews) {
      return;
    }

    if (!selectionScope) {
      setSelectedViewState(null);
      return;
    }

    const persistedViewId = readSavedViewSelectionPreference(selectionScope);

    if (!persistedViewId) {
      setSelectedViewState(null);
      return;
    }

    const persistedView = views.find((view) => view.id === persistedViewId);

    if (!persistedView) {
      clearSavedViewSelectionPreference(selectionScope);
      setSelectedViewState(null);
      return;
    }

    setSelectedViewState((currentView) =>
      currentView?.id === persistedView.id &&
      currentView.updatedAt === persistedView.updatedAt
        ? currentView
        : persistedView
    );
  }, [isLoadingViews, selectionScope, views]);

  const setSelectedView = useCallback(
    (view: SavedView | null) => {
      setSelectedViewState(view);

      if (!selectionScope) {
        return;
      }

      if (view) {
        persistSavedViewSelectionPreference(selectionScope, view.id);
        return;
      }

      clearSavedViewSelectionPreference(selectionScope);
    },
    [selectionScope]
  );

  useEffect(() => {
    syncSelectedViewFromStorage();
  }, [syncSelectedViewFromStorage]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handleSelectionStorageChange = () => {
      syncSelectedViewFromStorage();
    };

    window.addEventListener('storage', handleSelectionStorageChange);
    window.addEventListener(
      SAVED_VIEW_SELECTION_PREFERENCE_CHANGE_EVENT,
      handleSelectionStorageChange
    );

    return () => {
      window.removeEventListener('storage', handleSelectionStorageChange);
      window.removeEventListener(
        SAVED_VIEW_SELECTION_PREFERENCE_CHANGE_EVENT,
        handleSelectionStorageChange
      );
    };
  }, [syncSelectedViewFromStorage]);

  const userViews = useMemo(
    () => views.filter((view) => view.userId === user?.id && !view.isShared),
    [views, user?.id]
  );
  const sharedViews = useMemo(
    () => views.filter((view) => view.isShared),
    [views]
  );

  const isCurrentUserViewOwner = selectedView
    ? selectedView.userId === user?.id
    : false;
  const hasPermissionToEdit: boolean = isCurrentUserViewOwner;

  const updateView = async (view: SavedView): Promise<SavedView> => {
    try {
      const { id, userId, tenantId, createdAt, updatedAt, ...updateData } =
        view;
      const updatedView = await updateViewMutation({ id, ...updateData });

      if (selectedView && selectedView.id === updatedView.id) {
        setSelectedViewState(updatedView);
      }

      // TODO: check when implementing i18n
      showAlert('Vista actualizada exitosamente', 'success');

      return updatedView;
    } catch (error) {
      console.error('Error updating view:', error);
      // TODO: check when implementing i18n
      showAlert('Error al actualizar la vista', 'error');
      throw error;
    }
  };

  const deleteView = async (viewId: number): Promise<void> => {
    try {
      await deleteViewMutation(viewId);

      if (selectedView && selectedView.id === viewId) {
        setSelectedView(null);
      }

      // TODO: check when implementing i18n
      showAlert('Vista eliminada exitosamente', 'success');
    } catch (error) {
      console.error('Error deleting view:', error);
      // TODO: check when implementing i18n
      showAlert('Error al eliminar la vista', 'error');
      throw error;
    }
  };

  const loading = isLoadingViews || isLoadingRoles || isLoadingCurrentUser;
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen w-full">
        <Loading size="lg" />
      </div>
    );
  }

  return (
    <SavedViewContext.Provider
      value={{
        selectedView,
        setSelectedView,
        isViewSelected: !!selectedView,
        isCurrentUserViewOwner,
        hasPermissionToEdit,
        allViews: views,
        loading,
        isAdmin,
        userViews,
        sharedViews,
        updateView,
        deleteView,
      }}
    >
      {children}
    </SavedViewContext.Provider>
  );
};

export const useSavedViewContext = (): SavedViewContextProps => {
  const context = useContext(SavedViewContext);
  if (context === undefined) {
    throw new Error(
      'useSavedViewContext must be used within a SavedViewProvider'
    );
  }
  return context;
};
